import React, { useState } from 'react';
import classnames from 'classnames';
import { ModelEditorFactory, ModelTypeSelector } from '@leverege/ui-plugin';
import { useValueChange } from '@leverege/ui-hooks';
import { IdGen, Pane, Label, TextInput, Selector, Checkbox, Flex, NumericInput } from '@leverege/ui-elements';
import { ns } from '@leverege/i18n';
import { Colorizers } from '@leverege/ui-attributes';
import Model from "./ColumnModel.js";
import S from './Editor.css';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const {
  t: uiAttributesT
} = ns('ui-attributes.table.AttributeColumnEditor');
const {
  t
} = ns('molten.imagine.attributes.resourceImage.ResourceImageColumnModelEditor');
function ResourcesImageColumnModelEditor(props) {
  const {
    value,
    objectType
  } = props;
  const onModelChange = useValueChange(Model, props);
  const [ids] = useState(IdGen.createIds('attr', 18));
  if (value == null) {
    return null;
  }
  const formatter = Model.getFormatter(value);
  const formatterEditor = formatter && ModelEditorFactory.create(formatter, {
    eventData: 'setFormatter',
    onChange: onModelChange
  });
  const hFont = ModelEditorFactory.create(Model.getHeaderFont(value), {
    eventData: 'setHeaderFont',
    onChange: onModelChange
  });
  return /*#__PURE__*/_jsxs(Pane, {
    variant: "editor",
    className: S.grid,
    children: [/*#__PURE__*/_jsx("h3", {
      className: S.fill,
      children: uiAttributesT('column')
    }), /*#__PURE__*/_jsx(Label, {
      variant: "input",
      htmlFor: ids[0],
      children: uiAttributesT('name')
    }), /*#__PURE__*/_jsx(TextInput, {
      id: ids[0],
      eventData: "setHeaderLabel",
      hint: uiAttributesT('nameHint'),
      value: Model.getHeaderLabel(value),
      onChange: onModelChange
    }), /*#__PURE__*/_jsx(Label, {
      variant: "input",
      htmlFor: ids[1],
      children: uiAttributesT('width')
    }), /*#__PURE__*/_jsx(NumericInput, {
      id: ids[1],
      eventData: "setWidth",
      hint: uiAttributesT('widthHint'),
      value: Model.getWidth(value),
      onChange: onModelChange
    }), /*#__PURE__*/_jsx(Label, {
      variant: "input",
      htmlFor: ids[2],
      children: uiAttributesT('minWidth')
    }), /*#__PURE__*/_jsx(NumericInput, {
      id: ids[2],
      eventData: "setMinWidth",
      hint: uiAttributesT('minWidthHint'),
      value: Model.getMinWidth(value),
      onChange: onModelChange
    }), /*#__PURE__*/_jsx(Label, {
      variant: "input",
      htmlFor: ids[3],
      children: uiAttributesT('maxWidth')
    }), /*#__PURE__*/_jsx(NumericInput, {
      id: ids[3],
      eventData: "setMaxWidth",
      hint: uiAttributesT('maxWidthHint'),
      value: Model.getMaxWidth(value),
      onChange: onModelChange
    }), /*#__PURE__*/_jsx("h3", {
      className: S.fill,
      children: uiAttributesT('header')
    }), formatterEditor && /*#__PURE__*/_jsx(Label, {
      className: S.fill,
      variant: "input",
      children: uiAttributesT('format')
    }), formatterEditor != null && /*#__PURE__*/_jsx("div", {
      className: classnames(S.fill, S.sub),
      children: formatterEditor
    }), /*#__PURE__*/_jsx(Label, {
      variant: "input",
      htmlFor: ids[4],
      children: uiAttributesT('color')
    }), ModelTypeSelector.createSelectorAndEditor({
      editorProps: {
        id: ids[4],
        objectType
      },
      modelKey: 'colorizer',
      values: Colorizers.getColorizersFor(objectType),
      value: Model.getHeaderColor(value),
      eventData: 'setHeaderColor',
      onChange: onModelChange
    }, S.fill), hFont != null && /*#__PURE__*/_jsx(Label, {
      variant: "input",
      children: uiAttributesT('font')
    }), hFont != null && /*#__PURE__*/_jsx("div", {
      className: classnames(S.fill, S.sub),
      children: hFont
    }), /*#__PURE__*/_jsx(Label, {
      variant: "input",
      children: uiAttributesT('justify')
    }), /*#__PURE__*/_jsx(Selector, {
      value: Model.getHeaderJustify(value),
      eventData: "setHeaderJustify",
      values: [{
        value: null,
        name: uiAttributesT('none')
      }, {
        value: 'left',
        name: uiAttributesT('left')
      }, {
        value: 'center',
        name: uiAttributesT('center')
      }, {
        value: 'right',
        name: uiAttributesT('right')
      }],
      onChange: onModelChange
    }), /*#__PURE__*/_jsx(Label, {
      variant: "input",
      children: uiAttributesT('align')
    }), /*#__PURE__*/_jsx(Selector, {
      eventData: "setHeaderAlign",
      value: Model.getHeaderAlign(value),
      values: [{
        value: null,
        name: uiAttributesT('none')
      }, {
        value: 'top',
        name: uiAttributesT('top')
      }, {
        value: 'center',
        name: uiAttributesT('center')
      }, {
        value: 'bottom',
        name: uiAttributesT('bottom')
      }],
      onChange: onModelChange
    }), /*#__PURE__*/_jsxs(Flex, {
      variant: "rowM",
      className: S.fill,
      children: [/*#__PURE__*/_jsx(Checkbox, {
        id: ids[5],
        variant: "small",
        value: Model.getHeaderWrap(value),
        eventData: "setHeaderWrap",
        onChange: onModelChange
      }), /*#__PURE__*/_jsx(Label, {
        htmlFor: ids[5],
        variant: "input",
        children: uiAttributesT('textWrap')
      })]
    }), /*#__PURE__*/_jsx(Label, {
      variant: "input",
      htmlFor: ids[6],
      children: uiAttributesT('background')
    }), /*#__PURE__*/_jsx(TextInput, {
      id: ids[6],
      eventData: "setHeaderBackground",
      hint: uiAttributesT('backgroundHint'),
      value: Model.getHeaderBackground(value),
      onChange: onModelChange
    }), /*#__PURE__*/_jsx("h3", {
      className: S.fill,
      children: uiAttributesT('cell')
    }), /*#__PURE__*/_jsx(Label, {
      variant: "input",
      htmlFor: ids[7],
      children: uiAttributesT('emptyValue')
    }), /*#__PURE__*/_jsx(TextInput, {
      id: ids[7],
      eventData: "setEmptyValue",
      hint: uiAttributesT('emptyValueHint'),
      value: Model.getEmptyValue(value),
      onChange: onModelChange
    }), /*#__PURE__*/_jsx(Label, {
      variant: "input",
      htmlFor: ids[7],
      children: t('emptyIcon')
    }), /*#__PURE__*/_jsx(TextInput, {
      id: ids[7],
      eventData: "setEmptyIcon",
      hint: t('emptyIconHint'),
      value: Model.getEmptyIcon(value),
      onChange: onModelChange
    }), /*#__PURE__*/_jsx(Label, {
      variant: "input",
      htmlFor: ids[8],
      children: t('variant')
    }), /*#__PURE__*/_jsx(TextInput, {
      id: ids[8],
      eventData: "setVariant",
      hint: t('variantHint'),
      value: Model.getVariant(value),
      onChange: onModelChange
    }), /*#__PURE__*/_jsx(Label, {
      variant: "input",
      children: uiAttributesT('justify')
    }), /*#__PURE__*/_jsx(Selector, {
      value: Model.getJustify(value),
      eventData: "setJustify",
      values: [{
        value: null,
        name: uiAttributesT('none')
      }, {
        value: 'left',
        name: uiAttributesT('left')
      }, {
        value: 'center',
        name: uiAttributesT('center')
      }, {
        value: 'right',
        name: uiAttributesT('right')
      }],
      onChange: onModelChange
    }), /*#__PURE__*/_jsx(Label, {
      variant: "input",
      children: uiAttributesT('align')
    }), /*#__PURE__*/_jsx(Selector, {
      eventData: "setAlign",
      value: Model.getAlign(value),
      values: [{
        value: null,
        name: uiAttributesT('none')
      }, {
        value: 'top',
        name: uiAttributesT('top')
      }, {
        value: 'center',
        name: uiAttributesT('center')
      }, {
        value: 'bottom',
        name: uiAttributesT('bottom')
      }],
      onChange: onModelChange
    }), /*#__PURE__*/_jsx(Label, {
      variant: "input",
      htmlFor: ids[10],
      children: uiAttributesT('background')
    }), /*#__PURE__*/_jsx(TextInput, {
      id: ids[10],
      eventData: "setBackground",
      hint: uiAttributesT('backgroundHint'),
      value: Model.getBackground(value),
      onChange: onModelChange
    }), /*#__PURE__*/_jsx("h3", {
      className: S.fill,
      children: t('dialog')
    }), /*#__PURE__*/_jsx(Label, {
      variant: "input",
      htmlFor: ids[11],
      children: t('objectFit')
    }), /*#__PURE__*/_jsx(Selector, {
      eventData: "setObjectFit",
      value: Model.getObjectFit(value),
      values: [{
        value: null,
        name: t('none')
      }, {
        value: 'contain',
        name: t('contain')
      }, {
        value: 'cover',
        name: t('cover')
      }, {
        value: 'fill',
        name: t('fill')
      }, {
        value: 'scale-down',
        name: t('scaleDown')
      }],
      onChange: onModelChange
    }), /*#__PURE__*/_jsx(Label, {
      variant: "input",
      htmlFor: ids[12],
      children: t('imageWidth')
    }), /*#__PURE__*/_jsx(NumericInput, {
      id: ids[12],
      eventData: "setImageWidth",
      hint: t('imageWidthHint'),
      value: Model.getImageWidth(value),
      onChange: onModelChange
    }), /*#__PURE__*/_jsx(Label, {
      variant: "input",
      htmlFor: ids[13],
      children: t('imageHeight')
    }), /*#__PURE__*/_jsx(NumericInput, {
      id: ids[13],
      eventData: "setImageHeight",
      hint: t('imageHeightHint'),
      value: Model.getImageHeight(value),
      onChange: onModelChange
    }), /*#__PURE__*/_jsx(Label, {
      variant: "input",
      htmlFor: ids[18],
      children: t('dialogTitleIcon')
    }), /*#__PURE__*/_jsx(TextInput, {
      id: ids[18],
      eventData: "setDialogTitleIcon",
      hint: t('dialogTitleIconHint'),
      value: Model.getDialogTitleIcon(value),
      onChange: onModelChange
    })]
  });
}
export default /*#__PURE__*/React.memo(ResourcesImageColumnModelEditor);